import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Image } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import HTMLReactParser from 'html-react-parser';
import './FooterDisclaimer.css';

const FooterDisclaimer = ({ brokerData, brokerUrl }) => {
    const [brokerSpecificData, setBrokerSpecificData] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const currentYear = new Date().getFullYear();
    const disclaimer = brokerData?.flyerDisclaimer || '';
    const BrokerloID = brokerData?.loID || '';
    const BrokerCompanyLicenseInformation = brokerData?.companyName || '';
    const applyNowStateLicenseIn = brokerData?.applyNowStateLicenseIn || '';
    const companyCode = brokerData?.companyCode || '';
    const thumbnailPath = `${process.env.REACT_APP_CDN || ''}brokers-landingpages-resources/images/`;

    // Check if Texas is included in the license states (case insensitive)
    const includesTexas = applyNowStateLicenseIn.toLowerCase().includes('tx');

    // Fetch broker-specific data
    useEffect(() => {
        const fetchBrokerSpecificData = async () => {
            if (!companyCode) {
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_CDN}brokers-landingpages-resources/clientSpecificTermsPolicyDetails/${companyCode}.json`
                );
                
                if (response.ok) {
                    const data = await response.json();
                    // Assuming the JSON is an array and we need the first item
                    setBrokerSpecificData(Array.isArray(data) ? data[0] : data);
                } else {
                    console.warn('Failed to fetch broker-specific data:', response.status);
                }
            } catch (error) {
                console.error('Error fetching broker-specific data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBrokerSpecificData();
    }, [companyCode]);

    // Check if brokerSpecificDisclaimerURL is a variation of swmc.com/disclaimer
    const isSwmcDisclaimerUrl = (url) => {
        if (!url) return false;
        const normalizedUrl = url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
        return normalizedUrl.includes('swmc.com/disclaimer');
    };

    // Create the disclaimer link content properly
    const disclaimerLink = BrokerCompanyLicenseInformation ?
        BrokerCompanyLicenseInformation + " assumes complete liability and responsibility for the contents of the website." :
        HTMLReactParser(`Visit <a href="https://www.swmc.com/disclaimer/?blof=${BrokerloID}" target="_blank" rel="noopener noreferrer">https://www.swmc.com/disclaimer</a> for the full list of license information.`);

    return (
        <>
            <Container className="disclaimer-container">
                <Row className="disclaimer-text-row">
                    <Col>
                        <p className="disclaimer-text">{disclaimer}</p>
                    </Col>
                </Row>
            </Container>
            <footer className="footer-disclaimer">
                <Container className="Footer-container">
                    <Row className="copyright-row">
                        <Col className="text-center">
                            <p className="copyright">
                                © {currentYear} {" "}
                                <a
                                    href="https://www.celligence.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="celligence-link"
                                >
                                    Celligence International, LLC.
                                </a> All Rights Reserved.
                            </p>
                        </Col>
                    </Row>
                    <Row className="licensing-row">
                        <Col className="text-center">
                            <p className="licensing-info">
                                For licensing information, go to: <a href="https://www.nmlsconsumeraccess.org" target="_blank" rel="noopener noreferrer">www.nmlsconsumeraccess.org</a>.
                                <br /><br />
                                {/* Conditionally show Texas Complaint Notice if TX is in license states */}
                                {includesTexas && (
                                    <span>
                                        Please <Link to={`/${brokerUrl}/texas-disclosure/`} className="licensing-Texas-notice-link">Click Here</Link> to view Texas Complaint Notice and Servicing Disclosure.
                                        <br /><br />
                                    </span>
                                )}
                                {/* Conditionally show broker-specific disclaimer URL */}
                                {!loading && brokerSpecificData?.brokerSpecificDisclaimerURL && !isSwmcDisclaimerUrl(brokerSpecificData.brokerSpecificDisclaimerURL) && (
                                    <span>
                                        Visit <a 
                                            href={brokerSpecificData.brokerSpecificDisclaimerURL} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            {brokerSpecificData.brokerSpecificDisclaimerURL}
                                        </a> for the full list of license information.
                                        <br /><br />
                                    </span>
                                )}
                                {/* Show BrokerCompanyLicenseInformation disclaimer if available */}
                                {BrokerCompanyLicenseInformation && (
                                    <span>
                                        {BrokerCompanyLicenseInformation} assumes complete liability and responsibility for the contents of the website.
                                        <br />
                                    </span>
                                )}
                                {/* Show default disclaimer link if BrokerCompanyLicenseInformation is not available */}
                                {!BrokerCompanyLicenseInformation && (
                                    <span>
                                        {HTMLReactParser(`Visit <a href="https://www.swmc.com/disclaimer/?blof=${BrokerloID}" target="_blank" rel="noopener noreferrer">https://www.swmc.com/disclaimer</a> for the full list of license information.`)}
                                    </span>
                                )}
                            </p>
                        </Col>
                    </Row>
                    <Row className="logos-row">
                        <Col className="d-flex justify-content-center align-items-center logos-col">
                            <Image
                                src={`${thumbnailPath}equal-housing-opportunity.webp`}
                                alt="Equal Housing Opportunity"
                                className="equal-housing-logo"
                            />
                            <a
                                href={`https://www.swmc.com/?blof=${BrokerloID}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Image
                                    src={`${thumbnailPath}sunwest-partner.webp`}
                                    alt="SunWest Partner"
                                    className="sunwest-logo"
                                />
                            </a>
                        </Col>
                    </Row>
                </Container>
            </footer>
        </>
    );
};

export default FooterDisclaimer;